import axios from 'axios'
import type { OpenPermitListItem } from 'modules/permit/api/permitService'
import type { PermitTypeBadge } from './types'

export const normalizeGuid = (value?: string | null) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return /^[0-9a-fA-F-]{36}$/.test(trimmed) ? trimmed.toLowerCase() : null
}

export const resolveMenuRoleId = (menus: Array<Record<string, any>> | null | undefined, routePath: string) => {
  const normalizedRoutePath = routePath.trim().toLowerCase()

  for (const menu of menus ?? []) {
    const candidatePath = typeof menu?.routePath === 'string' ? menu.routePath.trim().toLowerCase() : ''
    if (!candidatePath) continue
    const normalizedCandidatePath = candidatePath.startsWith('/') ? candidatePath : `/${candidatePath}`
    if (normalizedCandidatePath !== normalizedRoutePath) continue

    const normalizedRoleId =
      normalizeGuid(menu?.roleId) ??
      normalizeGuid(menu?.role_id) ??
      normalizeGuid(menu?.id)

    if (normalizedRoleId) {
      return normalizedRoleId
    }
  }

  return null
}

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const responseMessage = (error.response?.data as { message?: string } | undefined)?.message
    return responseMessage || error.response?.statusText || error.message || fallback
  }
  return error instanceof Error ? error.message || fallback : fallback
}

export const formatDate = (value?: string | null, withTime = false) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}

export const formatDateRange = (start?: string | null, end?: string | null) => {
  return end ? `${formatDate(start, true)} - ${formatDate(end, true)}` : formatDate(start, true)
}

export const formatSupervisorTime = (start?: string | null, end?: string | null) => {
  if (!start && !end) return null
  if (start && end) return `${start} - ${end}`
  return start ? `${start} - ไม่ระบุ` : `ไม่ระบุ - ${end}`
}

export const canOpenPermitToday = (permit: OpenPermitListItem) => {
  if (!permit.plannedStart) return true
  const planned = new Date(permit.plannedStart)
  if (Number.isNaN(planned.getTime())) return true
  const today = new Date()
  return new Date(planned.getFullYear(), planned.getMonth(), planned.getDate()).getTime() <= new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
}

export const resolveAccessibleLocationIds = (
  user?: Record<string, any>,
  roles?: Array<Record<string, any>> | null,
  activeRoleId?: string | null,
) => {
  const results = new Set<string>()

  const normalizedActiveRoleId = normalizeGuid(activeRoleId)
  const roleRows = Array.isArray(roles) ? roles : []

  const matchingRoleRows = normalizedActiveRoleId
    ? roleRows.filter((role) => {
        const roleId =
          normalizeGuid(role?.roleId) ??
          normalizeGuid(role?.role_id) ??
          normalizeGuid(role?.id)
        return roleId === normalizedActiveRoleId
      })
    : roleRows

  const sourceRoleRows = matchingRoleRows.length > 0 ? matchingRoleRows : roleRows

  if (!normalizedActiveRoleId || matchingRoleRows.length === 0) {
    ;[
      user?.locationIds,
      user?.location_ids,
      user?.workAreaIds,
      user?.work_area_ids,
      user?.accessibleLocationIds,
      user?.accessible_location_ids,
      user?.locations,
    ].forEach((candidate) => {
      if (!Array.isArray(candidate)) return
      candidate.forEach((item) => {
        const normalized = normalizeGuid(typeof item === 'string' ? item : item?.locationId ?? item?.id)
        if (normalized) results.add(normalized)
      })
    })
  }

  sourceRoleRows.forEach((role) => {
    const normalized =
      normalizeGuid(role?.departmentId) ??
      normalizeGuid(role?.department_id) ??
      normalizeGuid(role?.locationId) ??
      normalizeGuid(role?.location_id)
    if (normalized) {
      results.add(normalized)
    }
  })

  return Array.from(results)
}

export const resolvePermitTypeBadge = (permitTypeCode?: string | null, permitTypeName?: string | null): PermitTypeBadge => {
  const normalizedCode = (permitTypeCode ?? '').trim().toUpperCase()
  const normalizedName = (permitTypeName ?? '').trim().toLowerCase()

  switch (normalizedCode) {
    case 'HOT_WORK':
      return { className: 'bg-red-600 text-white border-red-600', label: 'HOT WORK' }
    case 'COLD_WORK':
      return { className: 'bg-blue-600 text-white border-blue-600', label: 'COLD WORK' }
    case 'CONFINE_WORK':
      return { className: 'bg-violet-600 text-white border-violet-600', label: 'CONFINE WORK' }
    case 'EXCAVATION_WORK':
      return { className: 'bg-amber-600 text-white border-amber-600', label: 'EXCAVATION WORK' }
    case 'VIDEO_WORK':
      return { className: 'bg-fuchsia-600 text-white border-fuchsia-600', label: 'VIDEO WORK' }
    case 'PHOTO_WORK':
      return { className: 'bg-sky-600 text-white border-sky-600', label: 'PHOTO WORK' }
    case 'TEST_TEMPLATE':
      return { className: 'bg-slate-500 text-white border-slate-500', label: 'TEST TEMPLATE' }
    default:
      break
  }

  if (normalizedName.includes('hot work')) return { className: 'bg-red-600 text-white border-red-600', label: 'HOT WORK' }
  if (normalizedName.includes('cold work')) return { className: 'bg-blue-600 text-white border-blue-600', label: 'COLD WORK' }
  if (normalizedName.includes('confine')) return { className: 'bg-violet-600 text-white border-violet-600', label: 'CONFINE WORK' }

  return {
    className: 'bg-slate-100 text-slate-700 border-slate-200',
    label: permitTypeName || permitTypeCode || 'PERMIT',
  }
}
